import React from "react";
import { Phone, Mail, MessageCircle, MapPin, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/motion/Reveal";
import { BrandedForm } from "../components/BrandedForm";
import { SocialLinks } from "../components/SocialLinks";

export default function Contact() {
  const { t, tc } = useLang();
  const { settings } = useSettings();
  const methods = [
    { icon: MessageCircle, label: t.contact.whatsapp, value: settings.whatsapp ? settings.phone : "", href: `https://wa.me/${(settings.whatsapp || "").replace(/[^0-9]/g, "")}`, ltr: true },
    { icon: Phone, label: t.contact.callUs, value: settings.phone, href: `tel:${settings.phone || ""}`, ltr: true },
    { icon: Mail, label: t.contact.emailUs, value: settings.email, href: `mailto:${settings.email || ""}`, ltr: true },
    { icon: MapPin, label: t.contact.visitUs, value: tc(settings.address), href: "#map" },
  ].filter((method) => method.value);

  return (
    <>
      <SEO pageKey="contact" title={t.seo.contact.title} description={t.seo.contact.description} />
      <PageHeader pageKey="contact"
        overline={t.contact.overline}
        title={t.contact.title}
        sub={t.contact.sub}
        image="https://images.unsplash.com/photo-1533105079780-92b9be482077?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          {/* Methods */}
          <div className="order-3 lg:order-none lg:col-span-5">
            <Reveal>
              <div className="grid sm:grid-cols-2 gap-4">
                {methods.map((m, i) => (
                  <a
                    key={i}
                    href={m.href}
                    target={m.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`contact-method-${i}`}
                    className="group bg-lightgray rounded-2xl p-5 sm:p-6 border border-transparent hover:border-gold/40 hover:bg-green transition-colors duration-400 min-w-0"
                  >
                    <m.icon className="w-5 h-5 sm:w-6 sm:h-6 text-green group-hover:text-gold transition-colors mb-3 sm:mb-4" />
                    <div className="text-charcoal/45 group-hover:text-white/60 text-[11px] uppercase tracking-wider transition-colors">{m.label}</div>
                    <div
                      dir={m.ltr ? "ltr" : undefined}
                      className="font-display text-[15px] sm:text-lg text-charcoal group-hover:text-white transition-colors leading-snug mt-1.5 break-words [overflow-wrap:anywhere] [unicode-bidi:plaintext]"
                    >
                      {m.value}
                    </div>
                  </a>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div id="map" className="mt-4 rounded-2xl overflow-hidden border border-border h-[280px]">
                <iframe
                  title="Smart Farmland Office"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(settings.map_query || tc(settings.address) || "Istanbul,Turkey")}&output=embed`}
                  className="w-full h-full"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="order-1 lg:order-none lg:col-span-7">
            <Reveal>
              <div className="relative bg-white border border-border rounded-3xl p-7 sm:p-10 shadow-[0_24px_70px_rgba(23,64,42,0.10)] overflow-hidden" data-testid="contact-form">
                <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-green via-green-light to-gold" />
                <span className="text-gold text-[11px] uppercase tracking-[0.28em]">Smart Farmland</span>
                <h2 className="font-display text-3xl sm:text-4xl text-green-light mt-2 leading-tight">{t.contact.formTitle}</h2>
                <p className="text-charcoal/55 text-sm mt-3 mb-7 max-w-lg">{t.contact.formSub}</p>
                <BrandedForm />
                <p className="flex items-center gap-2 text-charcoal/45 text-xs mt-6">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-light" />
                  {t.contact.privacyNote}
                </p>
              </div>
            </Reveal>
          </div>

          {/* Mobile: social band sits right under the form */}
          <div className="order-2 lg:hidden flex flex-col items-center text-center gap-4 py-2" data-testid="contact-social-band-mobile">
            <h2 className="font-display text-2xl text-green-light">{t.contact.followUs}</h2>
            <SocialLinks theme="light" size="lg" place="contact-mobile" />
          </div>
        </div>
      </section>

      <section className="hidden lg:block bg-lightgray border-t border-border py-12 sm:py-16" data-testid="contact-social-band">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 flex flex-col items-center text-center gap-5">
          <h2 className="font-display text-2xl sm:text-3xl text-green-light">{t.contact.followUs}</h2>
          <SocialLinks theme="light" size="lg" place="contact" />
        </div>
      </section>
    </>
  );
}
